import moment from 'moment-timezone';
import Raven from 'raven-js';
import uuidv4 from 'uuid/v4';
import styles from './css/openchair.css';
import data from './data';
import views from './views';
import {
  getBookings,
  putBooking,
  postAppointment,
} from './helpers/dataHelpers';
import {
  fontLoader,
} from './helpers/domHelpers';
import { onButtonLoad } from './helpers/analytics';

const widgetCtrl = {
  init() {
    // TODO: decide on which elements on the page are customizable and add appropriate classes throughout
    fontLoader();
    const widgetDiv = document.getElementById('openchair-widget');
    widgetDiv.className = styles.openchairRoot;
    const token = widgetDiv.dataset.token;

    data.calendarStart = moment();
    data.calendarEnd = moment().add(13, 'weeks').endOf('week');
    const end = data.calendarStart.clone().endOf('week').add(2, 'weeks');
    data.clientUiid = uuidv4();

    return getBookings(data.calendarStart, end, token)
      .then((res) => {
        if (!('practice_name' in res) || !('locations' in res) || !res.locations.length) {
          return Promise.reject(new Error('Make sure that a practice has a name and at least one location'));
        }
        data.practiceName = res.practice_name;
        data.bookings = res.bookings;
        data.locations = res.locations;
        data.timezone = res.locations[0].timezone;
        data.cloud9_practice = res.cloud9_practice;
        data.currentLocation = data.locations[0];
        data.currentLocation.bookings = this.getBookingsForLocation(data.currentLocation);
        data.loadedUntil = end;
        if ('orthoconfirm' in widgetDiv.dataset) {
          data.orthoConfirm = widgetDiv.dataset.orthoconfirm;
        }
        views.init(widgetDiv);
        // call onLoad tracker
        onButtonLoad();
        return Promise.resolve();
      })
      .catch((err) => {
        console.log(err);
        Raven.captureException(err);
      });
  },

  loadNextPage() {
    const start = data.loadedUntil.clone().add(1, 'days');
    const end = data.loadedUntil.clone().add(2, 'weeks');
    return getBookings(start, end)
    .then((res) => {
      data.bookings = data.bookings.concat(res.bookings);
      data.currentLocation.bookings = this.getBookingsForLocation(data.currentLocation);
      data.loadedUntil = end;
    })
   .catch((err) => {
     // this.showError(); should we be showing this error to the user??
     Raven.captureException(err);
   });
  },

  cloud9Practice() {
    return data.cloud9_practice
  },

  getExtraFormInfo() {
    return data.orthoConfirm;
  },

  getTz() {
    return data.timezone || moment.tz.guess();
  },

  getLoadStatus() {
    return data.loadedUntil.diff(data.calendarEnd, 'weeks');
  },

  getAllBookings() {
    return data.bookings;
  },

  getBookingsForLocation(location = data.currentLocation) {
    return data.bookings.filter(booking => booking.location_id === location.id);
  },

  getBookingsForDay(day, bookings) {
    const bookingsForPeriod = [];
    for (let i = 0; i < bookings.length; i++) {
      if (day.isSame(bookings[i].start_datetime, 'day')) {
        bookingsForPeriod.push(bookings[i]);
      } else if (day.isBefore(bookings[i].start_datetime, 'day')) {
        break;
      }
    }
    return bookingsForPeriod;
  },

  getNumberBookingsAvailable(weekRef = moment().tz(this.getTz()), bookings = data.bookings) {
    const bookingsForPeriod = [];
    for (let i = 0; i < bookings.length; i++) {
      if (weekRef.isSame(bookings[i].start_datetime, 'week')) {
        bookingsForPeriod.push(bookings[i]);
      } else if (weekRef.isBefore(bookings[i].start_datetime, 'week')) {
        break;
      }
    }
    return bookingsForPeriod;
  },

  getNumberBookingsAvailableForLocations() {
    const weekRef = data.currentDay || undefined;
    const hotBookings = this.getNumberBookingsAvailable(weekRef);
    const hotBookingsPerLocation = {};
    for (let i = 0; i < hotBookings.length; i++) {
      if (hotBookingsPerLocation[hotBookings[i].location_id] === undefined) {
        hotBookingsPerLocation[hotBookings[i].location_id] = 0;
      }
      hotBookingsPerLocation[hotBookings[i].location_id]++;
    }
    return hotBookingsPerLocation;
  },

  findNextAvailableAppointment(startdate) {
    const bookings = this.getCurrentLocation().bookings;
    for (let i = 0; i < bookings.length; i++) {
      if (startdate.isSameOrBefore(bookings[i].start_datetime, 'day')) {
        return moment.tz(bookings[i].start_datetime, this.getTz());
      }
    }
    return null;
  },

  refreshBookings() {
    const end = data.calendarStart.clone().endOf('week').add(2, 'weeks');
    return getBookings(data.calendarStart, end)
    .then((res) => {
      data.clientUiid = uuidv4();
      data.bookings = res.bookings;
      data.currentLocation.bookings = data.bookings.filter(
        booking => booking.location_id === data.currentLocation.id
      );
      data.loadedUntil = end;
    })
    .catch((err) => {
      Raven.captureException(err);
    });
  },

  getOrthoConfirm() {
    return data.orthoConfirm;
  },

  getPracticeName() {
    return data.practiceName;
  },
  getLocations() {
    return data.locations;
  },

  getCurrentLocation() {
    return data.currentLocation;
  },

  setCurrentLocation(location) {
    data.currentLocation = location;
    data.currentLocation.bookings = data.bookings.filter(
      booking => booking.location_id === data.currentLocation.id
    );
  },

  holdBooking(booking) {
    return putBooking(booking.id, 'pending')
    .then(() => {
      this.setAppointmentInfo(booking);
    })
    .catch((err) => {
      this.showError();
      Raven.captureException(err);
    });
  },

  confirmBooking() {
    const booking = this.getAppointmentInfo();
    const patient = this.getPatientInfo();
    const body = JSON.stringify({
      patient: {
        first_name: patient['patient first name'],
        last_name: patient['patient last name'],
        guardian_name: patient['guardian name'] || null,
        zip_code: patient['zip code'] || null,
        phone: patient.phone,
        email: patient.email,
        message: patient.message || null,
        location_id: booking.location_id,
        client_uuid: data.clientUiid,
      },
      appointment: {
        appointment_datetime: booking.start_datetime,
      },
      booking: {
        id: booking.id,
      },
    });
    return postAppointment(body);
  },

  setAppointmentInfo(booking) {
    data.appointmentInfo = booking;
  },

  getAppointmentInfo() {
    return data.appointmentInfo;
  },
  setLatLngForLocations(latLngs) {
    latLngs.forEach((v, i) => {
      data.locations[i].latLng = v;
    });
  },

  getCurrentDay() {
    return data.currentDay;
  },

  setCurrentDay(day) {
    data.currentDay = day;
  },

  getPatientInfo() {
    return data.patientInfo;
  },
  setPatientInfo(patient) {
    data.patientInfo = patient;
  },

  showError(errorText = 'Sorry, something went wrong. \nPlease try again.') {
    views.handleError(errorText);
  },
};

// have to use module.exports to require this file
module.exports.widgetCtrl = widgetCtrl;



// WEBPACK FOOTER //
// ./src/widgetController.js